/**
 * useBiographyLibrary Hook
 *
 * Manages user biographies for personalized sessions.
 * Each user can have multiple biographies:
 * - One default biography for their own sessions
 * - Additional biographies for loved ones (gift recipients)
 *
 * Uses React Query for caching and state management.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/utils/apiClient';
import { toast } from 'sonner';

export interface UserBiography {
  id: string;
  user_id: string;
  biography_name: string;
  first_name: string | null;
  preferred_name: string | null;
  name_usage_preference: 'first' | 'preferred' | 'random';
  biography_text: string;
  is_default: boolean;
  created_at: string;
  updated_at: string;
}

export interface CreateBiographyData {
  biography_name: string;
  first_name?: string;
  preferred_name?: string;
  name_usage_preference?: 'first' | 'preferred' | 'random';
  biography_text: string;
  is_default?: boolean;
}

export interface UpdateBiographyData {
  biography_name?: string;
  first_name?: string;
  preferred_name?: string;
  name_usage_preference?: 'first' | 'preferred' | 'random';
  biography_text?: string;
  is_default?: boolean;
}

const BIOGRAPHY_QUERY_KEY = ['biographies'];

export function useBiographyLibrary() {
  const queryClient = useQueryClient();

  // Fetch all biographies
  const {
    data: biographiesData,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: BIOGRAPHY_QUERY_KEY,
    queryFn: async () => {
      const response = await apiClient.get('/biographies/');
      return response as { biographies: UserBiography[]; total: number };
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  const biographies = biographiesData?.biographies || [];

  // Get the default biography
  const defaultBiography = biographies.find((b) => b.is_default) || null;

  // Create biography mutation
  const createBiography = useMutation({
    mutationFn: async (data: CreateBiographyData) => {
      const response = await apiClient.post('/biographies/', data);
      return response as UserBiography;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: BIOGRAPHY_QUERY_KEY });
      toast.success('Biography created successfully');
    },
    onError: (error: Error) => {
      console.error('Failed to create biography:', error);
      toast.error(error.message || 'Failed to create biography');
    },
  });

  // Update biography mutation
  const updateBiography = useMutation({
    mutationFn: async ({
      id,
      updates,
    }: {
      id: string;
      updates: UpdateBiographyData;
    }) => {
      const response = await apiClient.put(`/biographies/${id}`, updates);
      return response as UserBiography;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: BIOGRAPHY_QUERY_KEY });
      toast.success('Biography updated successfully');
    },
    onError: (error: Error) => {
      console.error('Failed to update biography:', error);
      toast.error(error.message || 'Failed to update biography');
    },
  });

  // Delete biography mutation
  const deleteBiography = useMutation({
    mutationFn: async (id: string) => {
      await apiClient.delete(`/biographies/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: BIOGRAPHY_QUERY_KEY });
      toast.success('Biography deleted successfully');
    },
    onError: (error: Error) => {
      console.error('Failed to delete biography:', error);
      toast.error(error.message || 'Failed to delete biography');
    },
  });

  // Set default biography mutation
  const setDefaultBiography = useMutation({
    mutationFn: async (id: string) => {
      const response = await apiClient.post(`/biographies/${id}/set-default`);
      return response as UserBiography;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: BIOGRAPHY_QUERY_KEY });
      toast.success('Default biography updated');
    },
    onError: (error: Error) => {
      console.error('Failed to set default biography:', error);
      toast.error(error.message || 'Failed to set default biography');
    },
  });

  // Get a specific biography by ID
  const getBiography = (id: string) => {
    return biographies.find((b) => b.id === id) || null;
  };

  return {
    biographies,
    defaultBiography,
    isLoading,
    error,
    refetch,
    createBiography,
    updateBiography,
    deleteBiography,
    setDefaultBiography,
    getBiography,
    // Expose counts for UI
    biographyCount: biographies.length,
    maxBiographies: 10,
    canAddMore: biographies.length < 10,
  };
}

export default useBiographyLibrary;
